/**
 * Subscribing this browser to review reminders (FR-5.12).
 *
 * Named `push.ts` rather than `reminders.ts` because `Reminders.tsx` is the
 * screen: two files differing only in case are one file on a case-insensitive
 * filesystem, which TypeScript refuses outright. The repository already follows
 * this split elsewhere -- `photos.ts` beside `PhotoStrip.tsx`.
 *
 * Three things have to line up before a reminder can arrive: a registered
 * service worker, the user's permission, and a subscription the server has been
 * told about. Each can fail independently and for different reasons, so each
 * failure is reported as itself rather than as "could not enable reminders".
 *
 * Permission is requested only when the user asks for reminders, never on load.
 * A browser that is asked unprompted is a browser whose user clicks Block, and
 * `Notification.permission` is then `denied` permanently -- there is no second
 * chance from script, which makes an unprompted request a decision made once and
 * for the lifetime of the installation.
 */
import { apiFetch } from './api.js';
import { deviceId } from './clock.js';
import { reminderCadence, type ReminderCadence } from '@intentionalist/shared';

const VAPID_PUBLIC_KEY = import.meta.env['VITE_VAPID_PUBLIC_KEY'] ?? '';

export type ReminderState =
  /** The browser cannot do this at all -- no service worker, or no Push API. */
  | { readonly kind: 'unsupported' }
  /** Built without a VAPID key, so there is nothing to subscribe against. */
  | { readonly kind: 'unconfigured' }
  /** Permission was refused. Nothing script can do; the user must undo it. */
  | { readonly kind: 'blocked' }
  /** Able to ask, not yet subscribed. */
  | { readonly kind: 'available' }
  | { readonly kind: 'on'; readonly cadence: ReminderCadence };

export const supportsPush = (): boolean =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * What this browser can currently do.
 *
 * Reported from the browser's own state rather than from anything stored: the
 * user can revoke permission in site settings at any time, and a remembered
 * "reminders are on" would then be a lie the app keeps telling.
 */
export async function reminderState(): Promise<ReminderState> {
  if (!supportsPush()) return { kind: 'unsupported' };
  if (VAPID_PUBLIC_KEY === '') return { kind: 'unconfigured' };
  if (Notification.permission === 'denied') return { kind: 'blocked' };

  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();
  if (existing === null || existing === undefined) return { kind: 'available' };

  // The cadence is the server's record, not the browser's; what the browser
  // knows is only that a subscription exists.
  return { kind: 'on', cadence: stored() ?? 'daily' };
}

/**
 * Subscribes this browser and tells the server, at the chosen cadence.
 *
 * The order matters. The browser subscription is created first, because it is
 * the part that can be refused; telling the server about a subscription that was
 * never granted would leave a row addressing nothing. If the server call then
 * fails, the browser subscription is torn down again rather than left behind as
 * a subscription nobody will ever send to.
 */
export async function enableReminders(cadence: ReminderCadence): Promise<ReminderState> {
  const state = await reminderState();
  if (state.kind === 'unsupported' || state.kind === 'unconfigured' || state.kind === 'blocked') {
    return state;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { kind: 'blocked' };

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Required to be true by every current browser: a subscription that might
      // deliver silently is not allowed, which suits a product that should not
      // be doing anything behind the user's back anyway.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(VAPID_PUBLIC_KEY),
    }));

  const keys = extract(subscription);
  if (keys === null) {
    await subscription.unsubscribe();
    throw new Error('The browser gave a subscription without encryption keys.');
  }

  try {
    await apiFetch('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys,
        cadence,
        deviceId: deviceId(),
      }),
    });
  } catch (error) {
    // Leaving the browser subscribed with nothing on the server would be a
    // subscription that never receives anything and cannot be seen.
    await subscription.unsubscribe();
    throw error;
  }

  remember(cadence);
  return { kind: 'on', cadence };
}

/**
 * Stops reminders to this browser.
 *
 * Both halves, in the order that leaves nothing dangling: the server is told
 * first, so a failure there leaves the browser subscribed and the state
 * recoverable, rather than a live server row pushing to an endpoint the browser
 * has already discarded.
 */
export async function disableReminders(): Promise<ReminderState> {
  if (!supportsPush()) return { kind: 'unsupported' };

  await apiFetch('/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ deviceId: deviceId() }),
  });

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();

  forget();
  return { kind: 'available' };
}

// ---------------------------------------------------------------------------
// The chosen cadence, remembered locally
// ---------------------------------------------------------------------------

const CADENCE_KEY = 'intentionalist.reminders.cadence';

/**
 * Kept in `localStorage` as a convenience, not as a source of truth.
 *
 * The server decides when to send; this is only so the settings screen can show
 * what was chosen without a round trip. A wrong value here changes nothing about
 * what arrives.
 */
function remember(cadence: ReminderCadence): void {
  try {
    localStorage.setItem(CADENCE_KEY, cadence);
  } catch {
    // Private browsing, or storage refused. The setting still took effect.
  }
}

function forget(): void {
  try {
    localStorage.removeItem(CADENCE_KEY);
  } catch {
    // As above.
  }
}

function stored(): ReminderCadence | null {
  try {
    const parsed = reminderCadence.safeParse(localStorage.getItem(CADENCE_KEY));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Key and subscription encoding
// ---------------------------------------------------------------------------

/**
 * The VAPID public key as the Push API wants it: raw bytes, not base64url.
 *
 * `atob` rather than `Buffer`, since this runs in a browser -- and the padding
 * and the URL-safe alphabet both have to be undone by hand, which is the part
 * that is easy to get wrong and produces an `InvalidCharacterError` at subscribe
 * time rather than here.
 */
export function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

  /*
   * Built over an explicit `ArrayBuffer`, not via `Uint8Array.from`.
   *
   * `from` infers `Uint8Array<ArrayBufferLike>`, which the Push API's types
   * reject because `ArrayBufferLike` admits a `SharedArrayBuffer` and
   * `applicationServerKey` will not take one. The distinction is real rather
   * than pedantic: a shared buffer could be mutated by another thread between
   * the call and the subscribe.
   */
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** The two keys the server needs, base64url, or null if the browser withheld them. */
export function extract(subscription: PushSubscription): { p256dh: string; auth: string } | null {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (p256dh === null || auth === null) return null;

  return { p256dh: encode(p256dh), auth: encode(auth) };
}

const encode = (buffer: ArrayBuffer): string => {
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
